import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["labelledBy", "variant", "id", "maxHeight", "minHeight", "padding", "textAlign", "children", "elementRef", "isDisabled", "isSelected", "styles", "active", "unmountOnExit"];
var _dec, _class, _Panel;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { View } from '@instructure/ui-view';
import { passthroughProps } from '@instructure/ui-react-utils';
import { Transition } from '@instructure/ui-motion';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';

/**
---
parent: Tabs
id: Tabs.Panel
---
**/
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
let Panel = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_Panel = class Panel extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, {
      isHidden: this.isHidden
    });
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, {
      isHidden: this.isHidden
    });
  }
  get isHidden() {
    const _this$props3 = this.props,
      isDisabled = _this$props3.isDisabled,
      isSelected = _this$props3.isSelected;
    return !isSelected || !!isDisabled;
  }
  render() {
    const _this$props4 = this.props,
      labelledBy = _this$props4.labelledBy,
      variant = _this$props4.variant,
      id = _this$props4.id,
      maxHeight = _this$props4.maxHeight,
      minHeight = _this$props4.minHeight,
      padding = _this$props4.padding,
      textAlign = _this$props4.textAlign,
      children = _this$props4.children,
      elementRef = _this$props4.elementRef,
      isDisabled = _this$props4.isDisabled,
      isSelected = _this$props4.isSelected,
      styles = _this$props4.styles,
      active = _this$props4.active,
      unmountOnExit = _this$props4.unmountOnExit,
      props = _objectWithoutProperties(_this$props4, _excluded);
    return _jsx("div", {
      ...passthroughProps(props),
      css: styles === null || styles === void 0 ? void 0 : styles.panel,
      role: "tabpanel",
      tabIndex: 0,
      id: id,
      "aria-labelledby": labelledBy,
      "aria-hidden": this.isHidden ? 'true' : void 0,
      ref: this.handleRef,
      children: _jsx(Transition, {
        type: "fade",
        in: !this.isHidden,
        unmountOnExit: unmountOnExit,
        transitionExit: false,
        children: _jsx(View, {
          css: styles === null || styles === void 0 ? void 0 : styles.content,
          maxHeight: maxHeight,
          minHeight: minHeight,
          as: "div",
          padding: padding,
          textAlign: textAlign,
          children: children
        })
      })
    });
  }
}, _Panel.displayName = "Panel", _Panel.componentId = 'Tabs.Panel', _Panel.allowedProps = allowedProps, _Panel.propTypes = propTypes, _Panel.defaultProps = {
  isDisabled: false,
  textAlign: 'start',
  variant: 'default',
  isSelected: false,
  padding: 'small',
  active: false,
  unmountOnExit: true
}, _Panel)) || _class);
export default Panel;
export { Panel };